import React, { useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { LogOut, Users, Briefcase, Compass, ChevronDown, Building2, ShieldCheck, Lock } from 'lucide-react';
import { useInternshipAlerts } from '@/lib/useInternshipAlerts';

function RoleSwitch({ activeRole, compact }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  if (user?.role !== 'parent' || !user?.is_mentor) return null;

  const go = (role) => {
    localStorage.setItem('cpov_last_dashboard', role);
    navigate(role === 'mentor' ? '/mentor' : '/parent');
  };

  return (
    <div className={`flex items-center gap-0.5 rounded-full bg-[#F3F4F6] p-0.5 ${compact ? '' : 'p-1'}`} data-testid={compact ? 'role-switch-mobile' : 'role-switch'}>
      {[{ id: 'parent', label: 'Parent' }, { id: 'mentor', label: 'Mentor' }].map(r => (
        <button
          key={r.id}
          onClick={() => go(r.id)}
          className={`rounded-full font-semibold transition-colors ${compact ? 'px-2.5 py-1 text-[11px]' : 'px-3.5 py-1.5 text-xs'} ${
            activeRole === r.id ? 'bg-white text-[#1F2937] shadow-sm' : 'text-[#6B7280] hover:text-[#1F2937]'
          }`}
          data-testid={`switch-to-${r.id}${compact ? '-mobile' : ''}`}
        >
          {r.label}
        </button>
      ))}
    </div>
  );
}

function AccountMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const handleLogout = async () => { await logout(); navigate('/'); };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="flex items-center gap-2 h-9 px-2 rounded-full hover:bg-[#F8F9FA]" data-testid="workspace-account-menu">
          <div className="w-8 h-8 rounded-full bg-[#F97D6A] flex items-center justify-center">
            <span className="text-white text-xs font-semibold">
              {((user?.full_name || '').replace(/^(Dr\.?|Mr\.?|Mrs\.?|Ms\.?|Prof\.?)\s+/i, '').charAt(0)
                || user?.email?.charAt(0) || '?').toUpperCase()}
            </span>
          </div>
          <ChevronDown className="h-3.5 w-3.5 text-gray-400 hidden sm:block" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <div className="px-2 py-2">
          <p className="text-sm font-semibold text-[#1F2937]">{user?.full_name}</p>
          <p className="text-xs text-gray-500">{user?.email}</p>
        </div>
        <DropdownMenuSeparator />
        {(user?.is_partner || user?.role === 'partner') && (
          <DropdownMenuItem onClick={() => navigate('/partner')} data-testid="menu-partner-dashboard">
            <Building2 className="mr-2 h-4 w-4" /> Partner Dashboard
          </DropdownMenuItem>
        )}
        {user?.role === 'parent' && (
          <DropdownMenuItem onClick={() => { localStorage.setItem('cpov_last_dashboard', 'parent'); navigate('/parent'); }} data-testid="menu-parent-dashboard">
            <Users className="mr-2 h-4 w-4" /> Parent Home
          </DropdownMenuItem>
        )}
        {user?.is_mentor && user?.role === 'parent' && (
          <DropdownMenuItem onClick={() => { localStorage.setItem('cpov_last_dashboard', 'mentor'); navigate('/mentor'); }} data-testid="menu-mentor-dashboard">
            <Briefcase className="mr-2 h-4 w-4" /> Mentor Home
          </DropdownMenuItem>
        )}
        {!user?.is_mentor && user?.role === 'parent' && (
          <DropdownMenuItem onClick={() => navigate('/mentor/onboarding')} data-testid="menu-become-mentor">
            <Briefcase className="mr-2 h-4 w-4" /> Become a Mentor
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={() => navigate('/careers')} data-testid="menu-explore-careers">
          <Compass className="mr-2 h-4 w-4" /> Explore Careers
        </DropdownMenuItem>
        {user?.role === 'parent' && (
          <DropdownMenuItem onClick={() => navigate('/parent/safety-concerns')} data-testid="menu-safety-concerns">
            <ShieldCheck className="mr-2 h-4 w-4" /> Safety Concerns
          </DropdownMenuItem>
        )}
        {user?.role === 'parent' && (
          <DropdownMenuItem onClick={() => navigate('/parent/privacy-permissions')} data-testid="menu-privacy-permissions">
            <Lock className="mr-2 h-4 w-4" /> Privacy &amp; Permissions
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
          <LogOut className="mr-2 h-4 w-4" /> Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const Logo = ({ to }) => (
  <Link to={to} className="flex items-center" data-testid="workspace-logo">
    <span className="text-[19px] font-bold text-[#1F2937] font-['Outfit'] tracking-tight flex items-center">
      Careers<span className="bg-[#F97D6A] text-white text-[11px] font-extrabold px-1.5 py-[3px] rounded-[5px] leading-none tracking-wide ml-0.5">POV</span>
    </span>
  </Link>
);

export default function WorkspaceLayout({ nav, activeRole, children, testId }) {
  const location = useLocation();
  const { user } = useAuth();
  const { indicator } = useInternshipAlerts();
  // a small brand-coloured asterisk while an internship still needs the family
  const flagged = (item) => activeRole === 'parent' && item.id === 'internships' && indicator;
  const homePath = activeRole === 'mentor' ? '/mentor' : activeRole === 'partner' ? '/partner' : '/parent';

  // Keep the last-dashboard preference in sync with the workspace actually in use
  useEffect(() => {
    if (activeRole && activeRole !== 'partner') localStorage.setItem('cpov_last_dashboard', activeRole);
  }, [activeRole]);

  const isActive = (item) => (item.match ? item.match(location.pathname) : location.pathname === item.to);

  return (
    <div className="min-h-screen bg-white" data-testid={testId}>
      {/* Top header */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-[#EFF0F2]" data-testid="workspace-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 gap-4">
            <div className="flex items-center gap-8 min-w-0">
              <Logo to={homePath} />
              <nav className="hidden md:flex items-center gap-1" data-testid="workspace-nav">
                {nav.map(item => (
                  <Link
                    key={item.to + item.label}
                    to={item.to}
                    data-testid={`nav-${item.id}`}
                    className={`px-3.5 py-2 text-sm font-medium rounded-full transition-colors ${
                      isActive(item) ? 'text-[#1F2937] bg-[#F3F4F6]' : 'text-[#6B7280] hover:text-[#1F2937] hover:bg-[#F8F9FA]'
                    }`}
                  >
                    {item.label}
                    {flagged(item) && (
                      <span className="text-[#F97D6A] font-bold ml-0.5" title="Something needs your attention" data-testid="nav-internships-indicator">*</span>
                    )}
                  </Link>
                ))}
              </nav>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 shrink-0">
              <div className="hidden md:block"><RoleSwitch activeRole={activeRole} /></div>
              <div className="md:hidden"><RoleSwitch activeRole={activeRole} compact /></div>
              {user?.role === 'parent' && !user?.is_mentor && (
                <Button
                  onClick={() => { window.location.assign('/mentor/onboarding'); }}
                  className="hidden lg:inline-flex bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-full h-9 px-4 text-xs font-semibold"
                  data-testid="become-mentor-btn"
                >
                  Become a Mentor
                </Button>
              )}
              <AccountMenu />
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-7 sm:py-10 pb-28 md:pb-16">
        {children}
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-[#EFF0F2] pb-[env(safe-area-inset-bottom)]" data-testid="workspace-bottom-nav">
        <div className="flex items-stretch">
          {nav.map(item => {
            const active = isActive(item);
            return (
              <Link
                key={`bn-${item.to}${item.label}`}
                to={item.to}
                data-testid={`bottom-nav-${item.id}`}
                className={`flex-1 flex flex-col items-center gap-1 py-2.5 transition-colors ${active ? 'text-[#F97D6A]' : 'text-[#9CA3AF]'}`}
              >
                <item.icon className={`h-5 w-5 ${active ? 'stroke-[2.4]' : ''}`} />
                <span className="text-[10px] font-semibold leading-none">
                  {item.shortLabel || item.label}
                  {flagged(item) && (
                    <span className="text-[#F97D6A] font-bold" data-testid="bottom-nav-internships-indicator">*</span>
                  )}
                </span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
